import { FileJson, Layers, MoreVertical, Plus, Search, Settings2, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';

export function ContactTypesPage() {
  const navigate = useNavigate();
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const contactTypesQuery = trpc.contactTypes.list.useQuery();
  const contactTypes = contactTypesQuery.data?.items || [];
  const isLoading = contactTypesQuery.isLoading;

  const createMutation = trpc.contactTypes.create.useMutation({
    onSuccess: (newType) => {
      toast.success('New contact type created');
      navigate(`/org/${orgId}/workspace/${workspaceId}/settings/contact-types/${newType.id}/edit`);
    },
    onError: (error) => {
      toast.error(`Failed to create: ${error.message}`);
    },
  });

  const deleteMutation = trpc.contactTypes.delete.useMutation({
    onSuccess: () => {
      toast.success('Contact type deleted');
      contactTypesQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const handleCreate = () => {
    if (isReadOnly) return;
    createMutation.mutate({ label: 'New Contact Type' });
  };

  const handleDelete = (id: number) => {
    if (isReadOnly) return;
    if (
      confirm(
        'Are you sure you want to delete this contact type? This will affect all contacts using this type.'
      )
    ) {
      deleteMutation.mutate({ id });
    }
  };

  return (
    <main className="flex-1 p-6 lg:p-10 space-y-8 bg-slate-50/50 dark:bg-slate-950/50">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contact Types</h1>
          <p className="text-muted-foreground mt-1">
            Define custom schemas and metadata for your contacts.
          </p>
        </div>
        {!isReadOnly && (
          <Button
            className="w-full md:w-auto"
            onClick={handleCreate}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? (
              'Creating...'
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Define New Type
              </>
            )}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search types..." className="pl-10 bg-card" />
        </div>
      </div>

      <div className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[300px]">Type Name</TableHead>
              <TableHead>Custom Fields</TableHead>
              <TableHead>Created At</TableHead>
              {!isReadOnly && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array(3)
                .fill(0)
                .map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="h-4 w-40 bg-muted animate-pulse rounded" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell className="text-right">
                        <div className="h-8 w-8 ml-auto bg-muted animate-pulse rounded" />
                      </TableCell>
                    )}
                  </TableRow>
                ))
            ) : contactTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isReadOnly ? 3 : 4} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Layers className="h-10 w-10 text-muted-foreground opacity-20" />
                    <p className="text-sm text-muted-foreground">No contact types defined</p>
                    {!isReadOnly && (
                      <Button
                        variant="link"
                        className="text-xs text-primary"
                        onClick={handleCreate}
                      >
                        Create your first contact type
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              contactTypes.map((type) => (
                <TableRow key={type.id} className="group transition-colors">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded bg-indigo-500/10 flex items-center justify-center">
                        <Layers className="h-4 w-4 text-indigo-600" />
                      </div>
                      {type.label}
                    </div>
                  </TableCell>
                  <TableCell>
                    {type.customFieldsSchema ? (
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="font-normal flex items-center gap-1.5"
                        >
                          <FileJson className="h-3 w-3" />
                          {JSON.parse(type.customFieldsSchema).length} Fields
                        </Badge>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground opacity-50 italic">
                        Standard
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(type.createdAt * 1000).toLocaleDateString()}
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link
                              to={`/org/${orgId}/workspace/${workspaceId}/settings/contact-types/${type.id}/edit`}
                              className="flex items-center gap-2 cursor-pointer w-full"
                            >
                              <Settings2 className="h-4 w-4" />
                              Edit Schema
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer"
                            onClick={() => handleDelete(type.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete Type
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </main>
  );
}
